import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {AppState} from '../../state/reducers';
import {Router} from '@angular/router';
import * as TemplateActions from './actions';
import {getCurrentPractice} from '../../practices/state/selectors';
import {catchError, map, mergeMap, withLatestFrom} from 'rxjs/operators';
import {TemplateService} from '../templates.service';
import {Template} from '../../models/Template';
import { TranslatedMessageService } from '../../helpers/translated-message-service';
import {Noop} from '../../state/actions';
import {of} from 'rxjs';
import {DefaultWhatsappTemplate} from "../../models/DefaultWhatsappTemplate";

@Injectable()
export class WhatsappTemplatesEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private router: Router,
    private templateService: TemplateService,
    private messageService: TranslatedMessageService
  ) {}

  getTemplates$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.GetTemplates),
    withLatestFrom(this.store.select(getCurrentPractice)),
    mergeMap(([action, practice]) => {
      return this.templateService.getTemplates(action.syncWith360, practice, action.includeArchived)
        .pipe(
          map((result: Template[]) => {
            return TemplateActions.GetTemplatesSuccess({templates: result});
          }),
          catchError(() => {
            this.messageService.addTranslated({
              severity: 'error',
              summary: 'templates.effects.get_error.title',
              detail: 'templates.effects.get_error.detail',
              life: 5000
            });
            return of(TemplateActions.TemplateActionFailed());
          })
        );
    })
  ));

  getDefaultTemplates$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.GetDefaultTemplates),
    withLatestFrom(this.store.select(getCurrentPractice)),
    mergeMap(([action, practice]) => {
      return this.templateService.getDefaults(practice)
        .pipe(
          map((result: DefaultWhatsappTemplate[]) => {
            return TemplateActions.GetDefaultTemplatesSuccess({templates: result});
          }),
          catchError(() => {
            return of(Noop);
          })
        );
    })
  ));

  getCampaignTemplates$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.GetCampaignTemplates),
    withLatestFrom(this.store.select(getCurrentPractice)),
    mergeMap(([action, practice]) => {
      return this.templateService.getTemplates(false, practice, true)
        .pipe(
          map((result: Template[]) => {
            return TemplateActions.GetTemplatesSuccess({templates: result});
          }),
          catchError(() => {
            this.messageService.addTranslated({
              severity: 'error',
              summary: 'templates.effects.get_error.title',
              detail: 'templates.effects.get_error.detail',
              life: 5000
            });
            return of(TemplateActions.TemplateActionFailed());
          })
        );
    })
  ));

  getAutomationTemplates$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.GetAutomationTemplates),
    withLatestFrom(this.store.select(getCurrentPractice)),
    mergeMap(([action, practice]) => {
      return this.templateService.getTemplates(false, practice, true)
        .pipe(
          map((result: Template[]) => {
            return TemplateActions.GetTemplatesSuccess({templates: result});
          }),
          catchError(() => {
            this.messageService.addTranslated({
              severity: 'error',
              summary: 'Error',
              detail: 'An error occurred when getting templates',
              life: 5000
            });
            return of(TemplateActions.TemplateActionFailed());
          })
        );
    })
  ));

  getActiveTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.GetActivePaymentTemplate),
    withLatestFrom(this.store.select(getCurrentPractice)),
    mergeMap(([action, practice]) => {
      return this.templateService.getActivePaymentTemplate(practice)
        .pipe(
          map((result: Template | null) => {
            return TemplateActions.GetActivePaymentTemplateSuccess({template: result});
          })
        );
    })
  ));

  updateTemplateActive$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.UpdateTemplateActive),
    mergeMap((action) => {
      return this.templateService.updateTemplateActive(action.template)
        .pipe(
          map((result: Template) => {
            return TemplateActions.UpdateTemplateActiveSuccess({template: result});
          }),
          catchError(() => {
            this.messageService.addTranslated({
              severity: 'error',
              summary: 'templates.effects.update_error.title',
              detail: 'templates.effects.update_error.detail',
              life: 5000
            });
            return of(TemplateActions.TemplateActionFailed());
          })
        );
    })
  ));

  setTemplateToType$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.SetTemplateToType),
    mergeMap((action) => {
      return this.templateService.updateTemplateType(action.template, action.templateType)
        .pipe(
          map((result: Template[]) => {
            return TemplateActions.SetTemplateToTypeSuccess({updatedTemplates: result});
          }),
          catchError(() => {
            this.messageService.addTranslated({
              severity: 'error',
              summary: 'templates.effects.update_error.title',
              detail: 'templates.effects.update_error.detail',
              life: 5000
            });
            return of(TemplateActions.TemplateActionFailed());
          })
        );
    })
  ));

  deleteTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.DeleteTemplate),
    mergeMap((action) => {
      return this.templateService.deleteTemplate(action.template)
        .pipe(
          map((result) => {
            return TemplateActions.DeleteTemplateSuccess({template: action.template});
          }),
          catchError((error) => {
            // Use backend error message if available, otherwise fall back to translation
            const errorMessage = error?.error?.error || error?.message;
            if (errorMessage) {
              // Use the translated message service with a custom message object
              this.messageService.addTranslated({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 5000
              });
            } else {
              this.messageService.addTranslated({
                severity: 'error',
                summary: 'templates.effects.delete_error.title',
                detail: 'templates.effects.delete_error.detail',
                life: 5000
              });
            }
            return of(TemplateActions.TemplateActionFailed());
          })
        );
    })
  ));

  unarchiveTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.UnarchiveTemplate),
    mergeMap((action) => {
      return this.templateService.unarchiveTemplate(action.template)
        .pipe(
          map((result) => {
            return TemplateActions.UnarchiveTemplateSuccess({template: action.template});
          }),
          catchError(() => {
            this.messageService.addTranslated({
              severity: 'error',
              summary: 'templates.effects.restore_error.title',
              detail: 'templates.effects.restore_error.detail',
              life: 5000
            });
            return of(TemplateActions.TemplateActionFailed());
          })
        );
    })
  ));

  createTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.CreateTemplate),
    withLatestFrom(this.store.select(getCurrentPractice)),
    mergeMap(([action, practice]) => {
      return this.templateService.createTemplate(practice, action.dto)
        .pipe(
          map((result: Template) => {
            return TemplateActions.CreateTemplateSuccess({template: result});
          }),
          catchError(() => {
            this.messageService.addTranslated({
              severity: 'error',
              summary: 'templates.effects.create_error.title',
              detail: 'templates.effects.create_error.detail',
              life: 5000
            });
            return of(TemplateActions.TemplateActionFailed());
          })
        );
    })
  ));

  updateTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(TemplateActions.UpdateTemplate),
    withLatestFrom(this.store.select(getCurrentPractice)),
    mergeMap(([action, practice]) => {
      return this.templateService.updateTemplate(practice, action.dto)
        .pipe(
          map((result: Template) => {
            return TemplateActions.UpdateTemplateSuccess({template: result});
          }),
          catchError(() => {
            this.messageService.addTranslated({
              severity: 'error',
              summary: 'templates.effects.update_error_try_again.title',
              detail: 'templates.effects.update_error_try_again.detail',
              life: 5000
            });
            return of(TemplateActions.TemplateActionFailed());
          })
        );
    })
  ));
}
